// Reporting's PER ACCOUNT and PER DEPARTMENT sections (owner, 2026-11-04:
// "add another section of reports, called Per Account, with topics including
// Spend, Vendors, and Line Items. Do the same for Per Department. ... when we
// are offering visuals detailing line items, we should offer the Show Groups
// toggle.")
//
// Six charts, each a `ReportChart` the screen draws and `chartExport.ts`
// writes like any other. The two Line Items charts moved to the Line Items
// section as Line Items by Account and by Department (owner, 2026-11-04 —
// `lineItemCharts.ts`), under the same ids: the same figure, drawn once.
// They read `ChartData.accounts` and `ChartData.departments`, the two `GET /analytics/spend-by-code` reads the
// screen already makes; each row of that read carries the three counts
// (`docs/api.md` §16.8), so no new request is needed.
//
// THE MEASURES, over the real lines coded to the row's Account or Department
// under the Filters:
//   * Spend — the lines' totals summed (the same figure "Accounts by Spend"
//     drew, which is why that chart MOVED here rather than being duplicated:
//     `MOVED_CHART_IDS`, `withCodeCharts`).
//   * Vendors — how many distinct vendors billed at least one line to it.
//   * Line Items — how many lines are coded to it. With Show Group Rows on,
//     every group counts once and a line standing alone counts by itself, as
//     the line table shows them; off, every line counts by itself. A Tax or
//     Shipping line holds no Account or Department (D-420) and is in no bar
//     either way.
//
// No I/O here: the screen does the reads and hands the results in.
import type { AnalyticsSpendByCode } from '../api/types'
import type { BarRowData } from '../components/DashboardCharts'
import { lineItemsSection, type GroupRowsChart } from './lineItemCharts'
import { REPORT_CHARTS, compactMoney, wholeNumber, type ChartData, type ReportChart } from './reportCharts'
import { SECTION_COLORS } from './theme'

type SpendRow = AnalyticsSpendByCode['rows'][number]

/** A Per Account, Per Department or Line Items chart. `rows` also takes the Show Group Rows choice. */
type CodeChart = GroupRowsChart

const color = SECTION_COLORS.spend
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** Rows with a positive measure, largest first, then by label so ties read alphabetically. */
function bars(
  breakdown: AnalyticsSpendByCode | null,
  measure: (row: SpendRow) => number,
  display: (value: number) => string,
  title: (row: SpendRow) => string,
): BarRowData[] | null {
  if (breakdown === null) return null
  return [...breakdown.rows]
    .filter((row) => measure(row) > 0)
    .sort((a, b) => measure(b) - measure(a) || a.label.localeCompare(b.label))
    .map((row) => ({
      key: row.key,
      label: row.label,
      total: measure(row),
      display: display(measure(row)),
      segments: [{ color, value: measure(row) }],
      title: title(row),
    }))
}

/** The Line Items measure under the Show Group Rows choice. */
function lineItemCount(row: SpendRow, showGroupRows: boolean): number {
  return showGroupRows ? row.grouped_line_item_count : row.line_item_count
}

const SECTIONS = [
  { source: 'accounts', noun: 'Account', group: 'Per Account', filter: 'accounts' },
  { source: 'departments', noun: 'Department', group: 'Per Department', filter: 'departments' },
] as const

const CODE_CHARTS: readonly CodeChart[] = SECTIONS.flatMap((s): CodeChart[] => {
  const common = {
    group: s.group,
    labelHeader: s.noun,
    // Account and Department are the trailing and leading halves of a code
    // (D-85), so a bar click toggles its namesake criterion.
    filter: s.filter,
    truncated: (data: ChartData) => data[s.source]?.truncated === true,
  }
  return [
    {
      ...common,
      // The id "Accounts by Spend" / "Departments by Spend" had: the chart moved.
      id: `${s.source}-spend`,
      title: `Spend Per ${s.noun}`,
      valueHeader: 'Spend',
      rows: (data) =>
        bars(data[s.source], (r) => r.total, compactMoney, (r) =>
          `${r.label}: ${compactMoney(r.total)} across ${plural(r.count, 'invoice', 'invoices')}`,
        ),
    },
    {
      ...common,
      id: `${s.source}-vendors`,
      title: `Vendors Per ${s.noun}`,
      valueHeader: 'Vendors',
      rows: (data) =>
        bars(data[s.source], (r) => r.vendor_count, wholeNumber, (r) =>
          `${r.label}: ${plural(r.vendor_count, 'vendor', 'vendors')} across ${plural(r.count, 'invoice', 'invoices')}`,
        ),
    },
    {
      ...common,
      // Line Items Per Account / Per Department until the Line Items section
      // (owner, 2026-11-04): the chart moved there under its id.
      id: `${s.source}-line-items`,
      group: 'Line Items',
      title: `Line Items by ${s.noun}`,
      valueHeader: 'Line Items',
      lineItems: true,
      rows: (data, showGroupRows = true) =>
        bars(data[s.source], (r) => lineItemCount(r, showGroupRows), wholeNumber, (r) => {
          const n = lineItemCount(r, showGroupRows)
          return `${r.label}: ${plural(n, 'line item', 'line items')}${showGroupRows ? ', each group counted once' : ''}`
        }),
    },
  ]
})

/** The two "By Spend" charts the Spend Per Account and Spend Per Department charts replace, under the same ids. */
const MOVED_CHART_IDS: readonly string[] = ['accounts-spend', 'departments-spend']

/** The Line Items section: its own three charts around the two counts built above. */
const LINE_ITEM_CHARTS: readonly CodeChart[] = lineItemsSection(
  CODE_CHARTS.find((chart) => chart.id === 'accounts-line-items')!,
  CODE_CHARTS.find((chart) => chart.id === 'departments-line-items')!,
)

/**
 * The catalogue with the three new sections in it: the moved charts taken out
 * of By Spend, and Per Account, Per Department, then Line Items placed straight
 * after the last Per Company chart (at the end when there is none). Ids stay unique.
 */
function withCodeCharts(charts: readonly ReportChart[]): ReportChart[] {
  const kept = charts.filter((chart) => !MOVED_CHART_IDS.includes(chart.id))
  let at = -1
  kept.forEach((chart, index) => {
    if (chart.group === 'Per Company') at = index
  })
  const insertAt = at === -1 ? kept.length : at + 1
  const perCode = CODE_CHARTS.filter((chart) => chart.lineItems !== true)
  return [...kept.slice(0, insertAt), ...perCode, ...LINE_ITEM_CHARTS, ...kept.slice(insertAt)]
}

/**
 * EVERY CHART REPORTING OFFERS: `REPORT_CHARTS` with Per Account, Per
 * Department and Line Items after Per Company, and Accounts by Spend and Departments by Spend
 * gone from By Spend because Spend Per Account and Spend Per Department are the
 * same figures under the same ids. The rail and the chart lookup read this.
 */
export const REPORTING_CHARTS: readonly ReportChart[] = withCodeCharts(REPORT_CHARTS)

/** The chart with `id`, or the first when the id names none. */
export function reportingChartById(id: string): ReportChart {
  return REPORTING_CHARTS.find((chart) => chart.id === id) ?? REPORTING_CHARTS[0]!
}

/** True for a chart that counts line items, beside which the screen offers Show Group Rows. */
export function isLineItemsChart(chart: ReportChart): boolean {
  return (chart as CodeChart).lineItems === true
}

/**
 * The chart as drawn under the Show Group Rows choice: a plain `ReportChart`
 * whose `rows` and `truncated` already apply it and whose value heading says
 * which count it is, so the workspace and Download need no special case. Any
 * other chart comes back unchanged.
 */
export function withGroupRows(chart: ReportChart, showGroupRows: boolean): ReportChart {
  if (!isLineItemsChart(chart)) return chart
  const code = chart as CodeChart
  return {
    ...code,
    valueHeader: showGroupRows ? (code.groupedValueHeader ?? 'Line Items (Groups Counted Once)') : code.valueHeader,
    rows: (data) => code.rows(data, showGroupRows),
    truncated: (data) => code.truncated(data, showGroupRows),
  }
}
